import { IACMessage } from './iacMessage';
import { TYPES } from '../constants';
import * as Logger from '@navigator/frontend-logger';

import { Config, Navigator } from '..';

export interface IACUsecaseConfig {
  apps: string[];
  arguments?: {
    /*
     * required and optional are objects each key of it is the argument
     * name and the value is the IACArgument interface to get the type
     * {
     *   "categoryid": {
     *     "type": "string"
     *   }
     * }
     */
    required?: any;
    optional?: any;
  };
  deprecated?: string;
}

interface IACArgument {
  type: string;
}

/*
 * IACUsecase
 */
export class IACUsecase extends IACMessage {

  public config: IACUsecaseConfig = null;

  public validate(): boolean {
    if (!this.config) {
      Logger.error(`IAC use case validation failed. Unknown IAC use case named "${this.getName()}".`);
      return false;
    }

    // SAK: is that dependency necessary?
    if (!Navigator.hasApplication(this.getSender())) {
      Logger.error(`IAC use case validation failed. Unknown sender "${this.getSender()}".`);
      return false;
    }

    // If the IAC use case is specified as 3.1.0 or higher, we'll check the arguments.
    /* istanbul ignore next else */
    if (this.config.arguments && this.hasKeys(this.config.arguments)) {
      const payload = this.getPayload();
      const warnings: string[] = [];
      const required = this.config.arguments.required || {};
      const optional = this.config.arguments.optional || {};

      // The use case must define an argument map (plain object). Not more or less.
      if (payload.length !== 1 || !(typeof payload[0] === 'object')) {
        Logger.error(`IAC use case validation failed. No valid argument map set for "${this.getName()}".`);
        return false;
      }

      // Let's check if all required arguments are defined.
      Object.keys(required).forEach((key) => {
        const val: IACArgument = required[key];

        if (!(key in payload[0])) {
          warnings.push(`IAC use case validation failed. Required argument "${key}"` +
            ` is not defined for use case "${this.getName()}".`);
        }
        if ((val.type === 'array' && !Array.isArray(payload[0][key])) ||
            (val.type !== 'array' && typeof payload[0][key] !== val.type)) {
          warnings.push(`IAC use case validation failed. Required argument "${key}"` +
            ` must be a type of "${val.type}" for use case "${this.getName()}".`);
        }
      });

      // ... and the optional arguments ...
      Object.keys(optional).forEach((key) => {
        const val: IACArgument = optional[key];

        if (payload[0][key] && ((val.type === 'array' && !Array.isArray(payload[0][key])) ||
           (val.type !== 'array' && typeof payload[0][key] !== val.type))) {
          warnings.push(`IAC use case validation failed. Optional argument "${key}"` +
            ` must be a type of "${val.type}" for use case "${this.getName()}".`);
        }
      });

      // The sender is not allowed to add more arguments.
      Object.keys(payload[0]).forEach((key) => {
        if (!(key in required) && !(key in optional)) {
          warnings.push(`IAC use case validation failed. Argument "${key}"` +
            ` is not available for use case "${this.getName()}".`);
        }
      });

      // If we've detect some warnings, let's warn :-)
      if (warnings.length) {

        warnings.forEach((warning) => {
          Logger.warn(warning);
        });

        return false;
      }
    }

    // Oookay. The use case is still usable; but maybe deprecated?
    if (this.config.deprecated && Config.get().communication.usecases[this.config.deprecated]) {
      Logger.warn(`The IAC use case "${this.getName()}" is deprecated.` +
        ` Please use "${this.config.deprecated}" instead.`);
    } else if (this.config.deprecated) {
      Logger.warn(`The IAC use case "${this.getName()}" is deprecated.`);
    }

    return true;
  }

  /*
   * Marks the use case as RPE.
   */
  public asRPE(): IACUsecase {
    this.protocol.unsetFlag(TYPES.RPC);
    this.protocol.setFlags(TYPES.RPE);

    return this;
  }

  /*
   * Marks the use case as RPC.
   */
  public asRPC(): IACUsecase {
    this.protocol.unsetFlag(TYPES.RPE);
    this.protocol.setFlags(TYPES.RPC);

    return this;
  }

  /*
   * Marks the use case as INTERN.
   */
  public asIntern(): void {
    this.protocol.unsetFlag(TYPES.EXTERN);
    this.protocol.setFlags(TYPES.INTERN);
  }

  /*
   * Marks the use case as EXTERN.
   */
  public asExtern(): void {
    this.protocol.unsetFlag(TYPES.INTERN);
    this.protocol.setFlags(TYPES.EXTERN);
  }

  /* istanbul ignore next function */
  private hasKeys(object: any): boolean {
    for (const name in object) {
      if (Object.prototype.hasOwnProperty.call(object, name)) {
        return true;
      }
    }
    return false;
  }
}
